import React, { useState, useEffect } from "react";
import { Copy, Check, Share2, Link2, Lock, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ShareDialog({
  open,
  onOpenChange,
  kind = "document",
  title = "Shared Document",
  body = null,
  data = null,
  projectName = "",
}) {
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setToken("");
      return;
    }
    setLoading(true);
    setError(false);
    api
      .post("/share", {
        kind,
        title,
        body: body || null,
        data: data || null,
        project_name: projectName || null,
      })
      .then((r) => setToken(r.data.token))
      .catch(() => {
        setError(true);
        setToken("");
        toast.error("Could not create share link. Please retry.");
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const url = token ? `${window.location.origin}/share/${token}` : "";

  const copy = () => {
    if (!url) return;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Share link copied");
  };

  const openInNewTab = () => {
    if (url) window.open(url, "_blank", "noopener");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-slate-950 border-slate-800 text-slate-100 max-w-md rounded-sm"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400 mb-1 flex items-center gap-2">
            <Share2 className="h-3 w-3" />
            Share
          </div>
          <DialogTitle className="font-display text-xl">
            Share with stakeholders instantly
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            No login required to view · link is read-only
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <div
            className="flex items-center gap-2 bg-slate-900 border border-slate-800 rounded-sm px-3 py-2"
            data-testid="share-link-row"
          >
            <Link2 className="h-3.5 w-3.5 text-cyan-400 shrink-0" />
            {loading ? (
              <span className="flex items-center gap-1.5 text-xs font-mono text-slate-500 flex-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Creating share link…
              </span>
            ) : (
              <span
                className="text-xs font-mono text-slate-200 truncate flex-1"
                data-testid="share-link"
              >
                {url}
              </span>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={copy}
              disabled={!url}
              data-testid="share-copy-btn"
              className="h-7 px-2 text-slate-300 hover:text-cyan-300 hover:bg-slate-800 rounded-sm"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-emerald-400" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
            <Stat label="Shared with" value="Stakeholders" />
            <Stat label="Access" value="View only" />
            <Stat label="Expires" value="30 days" />
          </div>

          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Lock className="h-3 w-3" />
              {error
                ? "Connection error — try again"
                : "Audit-logged · revocable"}
            </div>
            <Button
              size="sm"
              onClick={openInNewTab}
              disabled={!url || loading}
              data-testid="share-open-btn"
              className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm disabled:opacity-50"
            >
              Open link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-slate-800 bg-slate-900/50 rounded-sm p-2">
      <div className="text-slate-600">{label}</div>
      <div className="text-cyan-300 mt-0.5 normal-case tracking-normal text-[11px]">
        {value}
      </div>
    </div>
  );
}
